import React, { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import { toast } from 'react-toastify'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, authHeaders } from '../../../../services/api'
import Card from '../../../../components/Card'
import Table from '../../../../components/Table'
import { endOfMonth, format, startOfMonth } from 'date-fns'
import maskUtils from '../../../../util/masks'
import formUtils from '../../../../util/form'
import { FiPause, FiRotateCcw, FiSearch, FiX } from 'react-icons/fi'
import masks from '../../../../util/masks'
import { Button, Select, Textbox } from '../../../../components/Form'
import yup from '../../../../services/yup'
import Spinner from '../../../../components/Spinner'

const statusNames = {
	pending: 'Pendente',
	authorized: 'Ativa',
	paused: 'Pausada',
	cancelled: 'Cancelada'
}

const initialInicio = startOfMonth(new Date())
const initialFim = endOfMonth(new Date())

const validation = yup.object({
	inicio: yup.string().date().required('Informe uma data.'),
	fim: yup.string().date().required('Informe uma data.')
})

export default function () {
	const [moduloSelecionado, setModuloSelecionado] = useState(null)
	const [assinaturas, setAssinaturas] = useState(null)

	const { data: modulos } = useQuery({
		queryKey: ['get-todos-os-modulos'],
		queryFn: async () => {
			const response = await api.get('modulo')

			const moduloInicial = response[0]

			setModuloSelecionado(moduloInicial.id)

			return response
		}
	})

	async function loadAssinaturas(data) {
		const response = await api.get('assinaturas', {
			...authHeaders(),
			params: {
				inicio: data?.inicio || format(startOfMonth(new Date()), 'yyyy-MM-dd HH:mm:ss'),
				fim: data?.fim || format(endOfMonth(new Date()), 'yyyy-MM-dd HH:mm:ss'),
				status: data?.status === undefined ? 1 : data.status,
				modulo: data?.modulo,
				plano: data?.plano
			}
		})

		const result = response.map(assinatura => ({
			id: assinatura.id,
			titular: assinatura.pessoa.nome,
			plano_descricao: assinatura.plano ? `${assinatura.plano.descricao} ${assinatura.plano.periodo}` : '-',
			modulo_descricao: assinatura.plano.modulo.descricao || '-',
			data_assinatura: format(new Date(assinatura.data_adesao), 'dd/MM/yyyy'),
			valor: maskUtils.maskApply.currency(assinatura.valor / 100),
			status: assinatura.status,
			status_descricao: statusNames[assinatura.status]
		}))

		setAssinaturas(result)
	}

	const { data: planos, refetch: refetchPlanos } = useQuery({
		queryKey: ['load-planos-do-modulo'],
		queryFn: async () => {
			const response = await api.get('plano', {
				params: {
					modulo_id: moduloSelecionado
				}
			})

			return response
		},
		enabled: !!moduloSelecionado
	})

	const { mutate: handleChangeAssinatura } = useMutation({
		mutationFn: async data => {
			await api.put(`assinatura/${data.id}`, data, authHeaders())

			toast.success('A assinatura foi atualizada.')

			loadAssinaturas()
		}
	})

	const { mutate: handleSearch } = useMutation({
		mutationFn: async values => {
			values = formUtils.extractFormValues(values)

			loadAssinaturas(values)
		}
	})

	useEffect(() => {
		loadAssinaturas()
	}, [])

	const modulosOptions = modulos?.map(modulo => ({
		label: modulo.descricao,
		value: modulo.id
	})) || []

	const planosOptions = planos?.map(plano => ({
		label: `${plano.descricao} ${plano.periodo}`,
		value: plano.id
	})) || []

	return (
		<div className="flex flex-col text-slate-800 sm:mx-4 sm:my-6 md:my-12 leading-loose">
			<Card className="flex flex-col">
				<h1 className="text-slate-800 mb-4">Assinaturas</h1>

				<Formik
					onSubmit={handleSearch}
					validationSchema={validation}
					initialValues={{
						inicio: format(initialInicio, 'dd/MM/yyyy'),
						fim: format(initialFim, 'dd/MM/yyyy'),
						modulo: modulosOptions[0],
						plano: null,
						status: { label: 'Ativo', value: 1 }
					}}
					enableReinitialize
				>
					{({ setFieldValue }) => (
						<Form className="flex w-full items-end">
							<Textbox
								label="Início adesão"
								name="inicio"
								mask={masks.date}
							/>

							<Textbox
								label="Fim adesão"
								name="fim"
								mask={masks.date}
							/>

							<Select
								label="Módulo"
								name="modulo"
								onChange={(selected, meta) => {
									setFieldValue(meta.name, selected)

									setModuloSelecionado(selected.value)

									setTimeout(() => {
										refetchPlanos()
									}, 400)
								}}
								options={modulosOptions}
							/>

							<Select
								label="Plano"
								name="plano"
								onChange={(selected, meta) => {
									setFieldValue(meta.name, selected)

									setModuloSelecionado(null)
								}}
								options={planosOptions}
							/>

							<Select
								label="Status"
								name="status"
								onChange={(selected, meta) => {
									setFieldValue(meta.name, selected)
								}}
								options={[
									{ label: 'Ativo', value: 1 },
									{ label: 'Inativo', value: 0 }
								]}
							/>

							<Button type="submit">
								Buscar
								<FiSearch size={16} />
							</Button>
						</Form>
					)}
				</Formik>

				{assinaturas ? (
					<Table
						headers={[
							{ value: 'Titular', name: 'titular' },
							{ value: 'Módulo', name: 'modulo_descricao', centered: true },
							{ value: 'Plano', name: 'plano_descricao', centered: true },
							{ value: 'Valor', name: 'valor', centered: true },
							{ value: 'Status', name: 'status_descricao', centered: true },
							{ value: 'Data de adesão', name: 'data_assinatura', centered: true }
						]}
						data={assinaturas}
						actions={item => {
							const actions = []

							switch (item.status) {
								case 'authorized':
									actions.push({
										action: item => {
											handleChangeAssinatura({
												...item,
												action: 'cancel'
											})
										},
										icon: FiX,
										iconSize: 16,
										name: 'Cancelar assinatura',
										confirmation: 'Você está prestes a <span class="font-bold">cancelar esta assinatura</span>. Isto removerá <span class="font-bold">definitivamente</span> o acesso ao produto contratado.',
										checkDisabled: item => item.status === 'cancelled',
										disabledTooltip: 'Não é possível cancelar esta assinatura.'
									})

									actions.push({
										action: item => {
											handleChangeAssinatura({
												...item,
												action: 'pause'
											})
										},
										icon: FiPause,
										iconSize: 16,
										name: 'Pausar assinatura',
										confirmation: 'Você está prestes a <span class="font-bold">pausar esta assinatura</span>. Isto removerá <span class="font-bold">temporariamente</span> o acesso ao produto contratado.<br/><br/>Deseja prosseguir?',
										checkDisabled: item => item.status !== 'authorized',
										disabledTooltip: 'Não é possível pausar esta assinatura.'
									})

									break

								case 'paused':
									actions.push({
										action: item => {
											handleChangeAssinatura({
												...item,
												action: 'cancel'
											})
										},
										icon: FiX,
										iconSize: 16,
										name: 'Cancelar assinatura',
										confirmation: 'Você está prestes a <span class="font-bold">cancelar esta assinatura</span>. Isto removerá <span class="font-bold">definitivamente</span> o acesso ao produto contratado.',
										checkDisabled: item => item.status === 'cancelled',
										disabledTooltip: 'Não é possível cancelar esta assinatura.'
									})

									actions.push({
										action: item => {
											handleChangeAssinatura({
												...item,
												action: 'reactive'
											})
										},
										icon: FiRotateCcw,
										iconSize: 16,
										name: 'Reativar assinatura',
										confirmation: 'Você está prestes a <span class="font-bold">reativar esta assinatura</span>.<br/><br/>Deseja prosseguir?',
										checkDisabled: item => item.status !== 'paused',
										disabledTooltip: 'Não é possível reativar esta assinatura.'
									})

									break

								default:
									break
							}

							return actions
						}}
					/>
				) : (
					<Spinner />
				)}
			</Card>
		</div>
	)
}
